import { cn } from '@savvy-kit/lib/utils';
import { UserRound } from 'lucide-react';

import type { AvatarProps as AvatarContainerProps } from './avatar-primitive';
import { Avatar as AvatarContainer, AvatarFallback, AvatarImage } from './avatar-primitive';

export { Avatar, AvatarBadge, AvatarFallback, AvatarGroup, AvatarGroupCount, AvatarImage } from './avatar-primitive';

type WrapperSize = 'xs' | 'sm' | 'default' | 'lg' | 'xl' | '2xl';

// Sizes the underlying base-ui primitive renders directly (via data-size).
const PRIMITIVE_SIZES = new Set<WrapperSize>(['sm', 'default', 'lg']);

// Wrapper-only sizes — passed through as className overrides on top of the primitive.
const SIZE_CLASS_OVERRIDES: Record<'xs' | 'xl' | '2xl', string> = {
  xs: 'size-5',
  xl: 'size-12',
  '2xl': 'size-14',
};

export type AvatarProps = Omit<AvatarContainerProps, 'size'> & {
  readonly size?: WrapperSize;
  readonly imageUrl?: string | null;
  readonly firstName?: string;
  readonly lastName?: string | null;
  readonly showFallbackIcon?: boolean;
  readonly customFallback?: React.ReactNode;
};

function Avatar({
  imageUrl,
  firstName,
  lastName,
  size = 'default',
  showFallbackIcon,
  customFallback,
  className,
}: AvatarProps) {
  const initials = `${firstName?.charAt(0) ?? ''}${lastName?.charAt(0) ?? ''}`.toUpperCase();
  const isPrimitiveSize = PRIMITIVE_SIZES.has(size);
  const primitiveSize = isPrimitiveSize ? (size as 'sm' | 'default' | 'lg') : 'default';
  const overrideClass = !isPrimitiveSize ? SIZE_CLASS_OVERRIDES[size as 'xs' | 'xl' | '2xl'] : undefined;

  return (
    <AvatarContainer size={primitiveSize} className={cn(overrideClass, className)}>
      <AvatarImage src={imageUrl ?? ''} alt="user-avatar" />
      {customFallback ? (
        customFallback
      ) : (
        <AvatarFallback className={cn(size === 'xs' && 'text-2xs')}>
          {showFallbackIcon ? <UserRound className="text-dark" /> : initials}
        </AvatarFallback>
      )}
    </AvatarContainer>
  );
}

export default Avatar;
